import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

const DISMISS_REMINDER = "cu_nudge_dismissed";
const SHOWN_EXIT = "cu_exit_shown";

// STEP 6 (smart reminder) + STEP 7 (desktop exit intent). Calm, one-time, dismissible.
export default function GrowthNudges() {
  const location = useLocation();
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(false);
  const [exit, setExit] = useState(false);

  const onCta = useCallback((source) => {
    track("cta_click", { cta_id: source, variant: "nudge" });
    track("talk_to_ai_click", { cta_id: source });
    setReminder(false); setExit(false);
    navigate("/ai-counseling");
  }, [navigate]);

  // Count college-detail views + 45s dwell -> reminder (once ever unless dismissed)
  useEffect(() => {
    if (localStorage.getItem(DISMISS_REMINDER) === "1" || location.pathname.startsWith("/ai-counseling")) return;
    if (/^\/college\/[^/]+/.test(location.pathname)) {
      const n = Number(sessionStorage.getItem("cu_college_views") || "0") + 1;
      sessionStorage.setItem("cu_college_views", String(n));
      if (n >= 2) setReminder(true);
    }
    const t = setTimeout(() => setReminder(true), 45000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Desktop exit-intent, once per browser
  useEffect(() => {
    if (window.innerWidth < 1024 || sessionStorage.getItem(SHOWN_EXIT) === "1") return;
    const handler = (e) => {
      if (e.clientY <= 0 && sessionStorage.getItem(SHOWN_EXIT) !== "1" && !location.pathname.startsWith("/ai-counseling")) {
        sessionStorage.setItem(SHOWN_EXIT, "1");
        setExit(true);
        track("exit_intent_shown", {});
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [location.pathname]);

  return (
    <>
      {reminder && !exit && (
        <div data-testid="smart-reminder" className="fixed bottom-6 left-6 z-40 w-[320px] max-w-[calc(100vw-3rem)] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
          <button data-testid="reminder-dismiss" onClick={() => { setReminder(false); localStorage.setItem(DISMISS_REMINDER, "1"); }} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          <p className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><Sparkles className="h-4 w-4 text-[#D4A24C]" /> Need help choosing?</p>
          <p className="mt-1 text-xs text-slate-500">Get a free, AI-matched college shortlist in 2 minutes.</p>
          <button data-testid="reminder-cta" onClick={() => onCta("smart_reminder")} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0B2A6B] py-2.5 text-xs font-bold text-white hover:bg-[#081E4D]">Start Free AI Counselling <ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {exit && (
        <div data-testid="exit-intent" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setExit(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button data-testid="exit-dismiss" onClick={() => setExit(false)} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            <Sparkles className="mx-auto h-10 w-10 text-[#D4A24C]" />
            <h3 className="mt-3 font-display text-xl font-extrabold text-slate-900">Still confused?</h3>
            <p className="mt-2 text-sm text-slate-600">Let our AI shortlist your best-fit colleges — free, no spam, 2 minutes.</p>
            <button data-testid="exit-cta" onClick={() => onCta("exit_intent")} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#D4A24C] px-6 py-3 text-sm font-bold text-white hover:bg-[#C08E3B]">Get My Free Recommendation <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
